/**
 * Security Utility module for GuideSantai WebReact
 * Handles Secret Key Encrypted Cookie Storage with obfuscated cookie names.
 */

// Obfuscated cookie names (Anti-Fingerprinting / Non-descript names)
export const AUTH_CREDS_KEY = '_gs_sess_id';
export const AUTH_USER_KEY = '_gs_usr_dat';

// Legacy keys to purge automatically
const LEGACY_KEYS = ['@loginCreds', '@login', 'loginCreds', 'loginToken'];

// Secret key used for symmetric encryption & obfuscation of cookie payloads
const SECRET_KEY = process.env.REACT_APP_AUTH_SECRET_KEY || 'GS_SecretKey_987654321_AuthSalt_v1';
const CIPHER_PREFIX = 'GS_ENC_SK_v1:';

// Purge any legacy cookie/storage keys on module initialization
if (typeof document !== 'undefined') {
  LEGACY_KEYS.forEach((key) => {
    eraseCookie(key);
    purgeWebStorage(key);
  });
}

/**
 * Applies Secret Key obfuscation & encoding to payload before storing in Cookie
 */
export function encryptWithSecretKey(data: any): string {
  try {
    const jsonStr = typeof data === 'string' ? data : JSON.stringify(data);
    
    // Salt encryption using Secret Key
    let result = '';
    for (let i = 0; i < jsonStr.length; i++) {
      const charCode = jsonStr.charCodeAt(i);
      const keyChar = SECRET_KEY.charCodeAt(i % SECRET_KEY.length);
      result += String.fromCharCode(charCode ^ keyChar);
    }
    
    const encoded = btoa(encodeURIComponent(result));
    return `${CIPHER_PREFIX}${encoded}`;
  } catch (error) {
    console.error('Failed to encrypt data with Secret Key', error);
    return '';
  }
}

/**
 * Decrypts ciphertext using Secret Key back to original object or primitive
 */
export function decryptWithSecretKey(cipherText: string | null): any {
  if (!cipherText || typeof cipherText !== 'string') return null;

  try {
    let rawEncoded = cipherText;
    if (rawEncoded.startsWith(CIPHER_PREFIX)) {
      rawEncoded = rawEncoded.slice(CIPHER_PREFIX.length);
    }

    const decodedResult = decodeURIComponent(atob(rawEncoded));
    
    // Reverse salt encryption using Secret Key
    let originalStr = '';
    for (let i = 0; i < decodedResult.length; i++) {
      const charCode = decodedResult.charCodeAt(i);
      const keyChar = SECRET_KEY.charCodeAt(i % SECRET_KEY.length);
      originalStr += String.fromCharCode(charCode ^ keyChar);
    }

    return JSON.parse(originalStr);
  } catch (error) {
    try {
      return JSON.parse(cipherText);
    } catch {
      return null;
    }
  }
}

/**
 * Writes cookie with secure attributes (SameSite=Lax, path=/)
 */
export function setCookie(name: string, value: string, days: number = 7): void {
  if (typeof document === 'undefined') return;

  try {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `; expires=${date.toUTCString()}`;
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const secureFlag = isHttps ? '; Secure' : '';

    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax${secureFlag}`;
  } catch (error) {
    console.error(`Failed to set cookie ${name}`, error);
  }
}

/**
 * Reads cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  try {
    const nameEQ = `${encodeURIComponent(name)}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  } catch (error) {
    console.error(`Failed to read cookie ${name}`, error);
    return null;
  }
}

/**
 * Erases cookie by expiring it
 */
export function eraseCookie(name: string): void {
  if (typeof document === 'undefined') return;

  try {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
  } catch (error) {
    console.error(`Failed to erase cookie ${name}`, error);
  }
}

/**
 * Purges key from localStorage & sessionStorage to ensure clean storage isolation
 */
function purgeWebStorage(key: string): void {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage availability errors
  }
}

/**
 * Stores item securely in encrypted Cookie with Secret Key
 */
export function setSecureItem(key: string, value: any): void {
  purgeWebStorage(key);

  if (value === null || value === undefined) {
    eraseCookie(key);
    return;
  }

  const encryptedValue = encryptWithSecretKey(value);
  setCookie(key, encryptedValue, 7); // Default 7 days retention
}

/**
 * Retrieves and decrypts item safely from Cookie
 */
export function getSecureItem(key: string): any {
  purgeWebStorage(key);
  const cookieVal = getCookie(key);
  return decryptWithSecretKey(cookieVal);
}

/**
 * Removes secure item from Cookie, localStorage, and sessionStorage
 */
export function removeSecureItem(key: string): void {
  purgeWebStorage(key);
  eraseCookie(key);
}

/**
 * Clears all authentication-related credentials from cookies & web storage
 */
export function clearAuthSession(): void {
  removeSecureItem(AUTH_CREDS_KEY);
  removeSecureItem(AUTH_USER_KEY);
  LEGACY_KEYS.forEach((k) => {
    eraseCookie(k);
    purgeWebStorage(k);
  });
}

/**
 * Checks whether a JWT token is expired (if valid JWT format)
 */
export function isTokenExpired(token?: string | null): boolean {
  if (!token || typeof token !== 'string') return true;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && typeof payload.exp === 'number') {
      const expirationMs = payload.exp * 1000;
      return Date.now() >= expirationMs;
    }
    return false;
  } catch {
    return false;
  }
}
